import { el, setChildren } from 'redom';

import DateTimeInputRow, { DateInputRow } from 'generics/components/date-time-input-row';
import SingleSelectInputBox from 'generics/components/single-select/native/input-box';
import InputRow from 'generics/components/text-input-row/row';
import TextInputBox from 'generics/components/text-input/text-input-box';

import {
  findIndexByPropertyValue
} from 'utilities/collection';
import {
  formatDateObjectToBrowserDate
} from 'utilities/date';

import recurringFrequencies from 'constants/recurring-frequencies';

import {
  formatDateTime
} from './utilities';

import './index.scss';

const endRepeatOptions = {
  NEVER: 'Never',
  AFTER: 'After',
  ON: 'On'
};

const modes = {
  CREATE: 'create',
  EDIT: 'edit'
};

class ScheduleSection {

  constructor(configuration) {

    this.readOnly = configuration.readOnly;
    this.previousEvent = structuredClone(configuration.event);
    this.event = configuration.event;
    this.onChange = configuration.onChange || function noop() {};
    this.skipRecurring = configuration.skipRecurring || false;
    this.inputs = [];
    this.enforceSameDate = configuration.enforceSameDate || false;
    this.mode = configuration.modes || modes.CREATE;

    this.startDateTimeInputRow = null;
    this.endDateTimeInputRow = null;
    this.repeatInputBox = null;
    this.endRepeatInputBox = null;
    this.endRepeatOnDateInputBox = null;
    this.endRepeatAfterInputRow = null;

    this.initializeInputs();

    this.el = el('#calendar-activity-schedule-section-main', this.inputs);

    this.updateInputs();

  }

  initializeInputs() {

    this.startDateTimeInputRow = new DateTimeInputRow({
      disabled: this.readOnly === true,
      label: 'Start Time',
      placeholder: {
        date: 'Start Date',
        time: 'Start Time'
      },
      value: formatDateTime(this.event.startDateTime),
      onChange: this.handleStartDateTimeChange.bind(this)
    });

    this.endDateTimeInputRow = new DateTimeInputRow({
      disabled: this.readOnly === true,
      label: 'End Time',
      placeholder: {
        date: 'End Date',
        time: 'End Time'
      },
      value: formatDateTime(this.event.endDateTime),
      onChange: this.handleEndDateTimeChange.bind(this)
    });

    this.inputs.push(
      this.startDateTimeInputRow,
      this.endDateTimeInputRow
    );

    if (this.skipRecurring === true) {
      return;
    }

    this.repeatInputBox = new SingleSelectInputBox(this.buildRepeatInputOptions());
    this.endRepeatInputBox = new SingleSelectInputBox(this.buildEndRepeatInputOptions());

    this.inputs.push(
      new InputRow({ label: 'Repeat', input: this.repeatInputBox }),
      new InputRow({ label: 'End Repeat', input: this.endRepeatInputBox })
    );

    this.initializeEndRepeatOnDateInput();
    this.initializeEndRepeatAfterInput();

  }

  initializeEndRepeatOnDateInput() {

    const until = this.event.recurring.until || Date.now();
    const value = formatDateObjectToBrowserDate(new Date(until));

    this.endRepeatOnDateInputRow = new DateInputRow({
      disabled: this.readOnly === true,
      placeholder: 'End Repeat On',
      label: 'End Repeat On',
      value: value,
      onChange: this.handleEndRepeatOnDateChange.bind(this)
    });

  }

  initializeEndRepeatAfterInput() {

    const count = this.event.recurring.count;

    this.endRepeatAfterInputBox = new TextInputBox({
      disabled: this.readOnly === true,
      placeholder: 'Enter number of times to repeat',
      value: count,
      type: 'number',
      onChange: this.handleEndRepeatAfterChange.bind(this)
    });

    this.endRepeatAfterInputRow = el('.form-input-row', [
      el('label.field-label', 'End Repeat After'),
      el('.input-box-container', [this.endRepeatAfterInputBox,
        el('label', 'times')
      ])
    ]);

  }

  getRepeatInputOptions() {

    const options = [{ label: 'Never', value: recurringFrequencies.NEVER }, {
      label: 'Daily',
      value: recurringFrequencies.DAILY
    }, { label: 'Weekly', value: recurringFrequencies.WEEKLY }, {
      label: 'Monthly',
      value: recurringFrequencies.MONTHLY
    }, { label: 'Yearly', value: recurringFrequencies.YEARLY }];

    return options;

  }

  getSelectedRepeatIndices() {

    const index = findIndexByPropertyValue(this.getRepeatInputOptions(), 'value', this.event.recurring.freq);

    if (index === -1) {
      return [];
    }

    return [index];

  }

  buildRepeatInputOptions() {

    const options = {
      disabled: this.readOnly === true,
      placeholder: 'Repeat',
      options: this.getRepeatInputOptions(),
      selectedOptionIndices: this.getSelectedRepeatIndices(),
      onChange: this.handleRepeatChange.bind(this)
    };

    return options;

  }

  getEndRepeatInputOptions() {

    const options = [{ label: 'Never', value: endRepeatOptions.NEVER },
      // {
      //   label: 'After',
      //   value: endRepeatOptions.AFTER
      // }, 
      { label: 'On', value: endRepeatOptions.ON }
    ];

    return options;

  }

  getSelectedEndRepeatIndices() {

    const recurring = this.event.recurring;
    let value = endRepeatOptions.NEVER;

    if (typeof recurring.until === 'number') {
      value = endRepeatOptions.ON;
    } else if (typeof recurring.count === 'number') {
      value = endRepeatOptions.AFTER;
    }

    const index = findIndexByPropertyValue(this.getEndRepeatInputOptions(), 'value', value);

    if (index === -1) {
      return [];
    }

    return [index];

  }

  buildEndRepeatInputOptions() {

    const options = {
      disabled: this.readOnly === true,
      placeholder: 'End Repeat',
      options: this.getEndRepeatInputOptions(),
      selectedOptionIndices: this.getSelectedEndRepeatIndices(),
      onChange: this.handleEndRepeatChange.bind(this)
    };

    return options;

  }

  updateRecurringInputs() {

    this.inputs.push(new InputRow({ label: 'Repeat', input: this.repeatInputBox }));

    if (this.event.recurring.freq !== recurringFrequencies.NEVER) {

      this.endRepeatInputBox.configure(this.buildEndRepeatInputOptions());
      this.inputs.push(new InputRow({ label: 'End Repeat', input: this.endRepeatInputBox }));

      const until = this.event.recurring.until;
      const count = this.event.recurring.count;

      if (typeof until === 'number') {
        this.inputs.push(this.endRepeatOnDateInputRow);
        this.endRepeatOnDateInputRow.inputBox.setValue(formatDateObjectToBrowserDate(new Date(until)));
      }

      if (typeof count === 'number') {
        this.inputs.push(this.endRepeatAfterInputRow);
        this.endRepeatAfterInputBox.setValue(count);
      }

    }

  }

  updateInputs() {

    this.inputs = [];

    this.inputs.push(this.startDateTimeInputRow);
    this.inputs.push(this.endDateTimeInputRow);

    if (this.skipRecurring === false) {

      this.updateRecurringInputs();

    }

    setChildren(this.el, this.inputs);

  }

  handleEndRepeatOnDateChange(event) {

    this.event.recurring.until = event.target.valueAsDate.getTime();

    const modified = this.event.recurring.until !== this.previousEvent.recurring.until;

    this.onChange({ value: { ...this.event }, modified });

  }

  handleEndRepeatAfterChange(value) {

    this.event.recurring.count = value;

    const modified = this.event.recurring.count !== this.previousEvent.recurring.count;

    this.onChange({ value: { ...this.event }, modified });

  }

  handleStartDateTimeChange(value) {

    this.event.startDate = value.date;
    this.event.startTime = value.time;
    this.event.startDateTime = value.dateTime;

    if (this.enforceSameDate === true) {
      this.endDateTimeInputRow.input.setValue({ date: value.date });
    }

    const modified = this.event.startDateTime !== this.previousEvent.startDateTime;

    this.onChange({ value: { ...this.event }, modified });

  }

  handleEndDateTimeChange(value) {

    this.event.endDate = value.date;
    this.event.endTime = value.time;
    this.event.endDateTime = value.dateTime;

    if (this.enforceSameDate === true) {
      this.startDateTimeInputRow.input.setValue({ date: value.date });
    }

    const modified = this.event.endDateTime !== this.previousEvent.endDateTime;

    this.onChange({ value: { ...this.event }, modified });

  }

  handleRepeatChange({ values }) {

    const repeat = values[0];

    this.event.recurring.freq = repeat;

    if (repeat === recurringFrequencies.NEVER) {
      this.event.recurring.until = void 0;
      this.event.recurring.count = void 0;
    }

    this.updateInputs();

    const modified = JSON.stringify(this.event.recurring) !== JSON.stringify(this.previousEvent.recurring);

    this.onChange({ value: { ...this.event }, modified });

  }

  handleEndRepeatChange({ values }) {

    const endRepeat = values[0];

    if (endRepeat === endRepeatOptions.NEVER) {
      this.event.recurring.until = void 0;
      this.event.recurring.count = void 0;
    } else if (endRepeat === endRepeatOptions.AFTER) {
      this.event.recurring.until = void 0;
      this.event.recurring.count = 1;
    } else if (endRepeat === endRepeatOptions.ON) {
      this.event.recurring.until = Date.now();
      this.event.recurring.count = void 0;
    }

    this.updateInputs();

    const modified = JSON.stringify(this.event.recurring) !== JSON.stringify(this.previousEvent.recurring);

    this.onChange({ value: { ...this.event }, modified });

  }

  setErrors(errors) {

    for (let error of errors) {

      if (error.instancePath.indexOf('startDateTime') !== -1) {
        if (error.keyword === 'isValidStartDate') {
          this.startDateTimeInputRow.input.setError({ date: 'Please select start date from today or later' });
        } else if (error.keyword === 'isValidStartDateTime') {
          this.startDateTimeInputRow.input.setError({ time: 'Please select start time that is at least 30 minutes from now' });
        } else if (error.keyword === 'isValidRecentDate') {
          this.startDateTimeInputRow.input.setError({ date: 'Please select start time that is not older than 30 days from today' });
        }
      } else if (error.instancePath.indexOf('endDateTime') !== -1) {
        if (error.keyword === 'isValidEndDate') {
          this.endDateTimeInputRow.input.setError({ date: 'Please select end date same as start date or later' });
        } else if (error.keyword === 'isValidEndDateTime') {
          this.endDateTimeInputRow.input.setError({ time: 'Please select end time that is at least 15 minutes greater than start time' });
        }
      } else if (error.instancePath.indexOf('recurring') !== -1) {
        this.repeatInputBox.setError('Please select repeat');
      } else if (error.instancePath.indexOf('recurring/until') !== -1) {
        this.endRepeatOnDateInputBox.setError('Please select end repeat on date');
      } else if (error.instancePath.indexOf('recurring/count') !== -1) {
        this.endRepeatAfterInputBox.setError('Please enter end repeat after');
      }

    }

  }

  clearErrors() {

    const inputs = [
      this.startDateTimeInputRow.input,
      this.endDateTimeInputRow.input
    ];

    if (this.skipRecurring === false) {
      inputs.push(
        this.repeatInputBox,
        this.endRepeatInputBox,
        this.endRepeatOnDateInputRow.inputBox,
        this.endRepeatAfterInputBox);
    }

    for (let input of inputs) {
      input.clearError();
    }

  }

  populateRecurringValue(value) {

    value.recurring = {
      freq: this.repeatInputBox.getValue() || recurringFrequencies.NEVER
    };

    if (value.recurring.freq !== recurringFrequencies.NEVER) {

      if (this.endRepeatInputBox.getValue() === endRepeatOptions.AFTER) {
        value.recurring.count = this.endRepeatAfterInputBox.getValue();
      } else if (this.endRepeatInputBox.getValue() === endRepeatOptions.ON) {
        value.recurring.until = this.endRepeatOnDateInputRow.inputBox.input.getValueAsDate().getTime();
      }

    }

  }

  getValue() {

    const startDateAndTime = this.startDateTimeInputRow.input.getValue();
    const endDateAndTime = this.endDateTimeInputRow.input.getValue();

    const value = {
      startDate: startDateAndTime.date,
      startTime: startDateAndTime.time,
      endDate: endDateAndTime.date,
      endTime: endDateAndTime.time,
      startDateTime: startDateAndTime.dateTime,
      endDateTime: endDateAndTime.dateTime
    };

    if (this.skipRecurring === false) {
      this.populateRecurringValue(value);
    }

    return value;

  }

}

ScheduleSection.modes = modes;

export default ScheduleSection;